import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sanitizeSearchInput, sanitizeString, validateEmail } from '../utils/security';
import { formatTechServiceLocation } from '../utils/techServiceHelpers';
import {
  fetchUserById,
  updateUser,
} from '../services/usersService';
import {
  searchTechServices,
  fetchTechServiceDetail,
} from '../services/techServicesService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  ORDER_CREATION_PERMISSION_OPTIONS,
  ORDER_CREATION_PERMISSION_DEFAULT,
} from '../constants/orderCreationPermission';
import { USER_ROLE_OPTIONS } from '../constants/userRoles';

const USER_TABS = [
  { key: 'credentials', label: 'Kullanıcı Bilgileri' },
  { key: 'personal', label: 'Kişisel Bilgiler' },
];

const roleOptions = USER_ROLE_OPTIONS;

const initialUserState = {
  id: '',
  kullaniciAdi: '',
  sifre: '',
  rol: 'sales',
  techService: '',
  aktif: true,
  isTest: false,
  ad: '',
  soyad: '',
  bayiAdi: '',
  mail: '',
  telefon: '',
  orderCreationPermission: ORDER_CREATION_PERMISSION_DEFAULT,
};

const PHONE_REGEX = /^(\+90|0)?[0-9]{10}$/;

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) {
    return fallback;
  }

  if (error?.status === 404) {
    return 'API endpoint bulunamadı.';
  }

  if (error?.status === 401) {
    return 'Kimlik doğrulama hatası. Lütfen yeniden giriş yapın.';
  }

  if (error?.status === 403) {
    return 'Bu işlem için yetkiniz yok.';
  }

  const hatalar = error?.data?.hatalar;
  if (Array.isArray(hatalar) && hatalar.length > 0) {
    const messages = hatalar
      .map((h) => h.mesaj || h.message)
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join('. ');
    }
  }

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const UserFormPage = () => {
  const { confirm } = useConfirmDialog();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formState, setFormState] = useState(initialUserState);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [activeTab, setActiveTab] = useState(USER_TABS[0].key);
  const [sectionStatus, setSectionStatus] = useState({
    credentials: null,
    personal: null,
  });
  const [savingSection, setSavingSection] = useState(null);
  const [techServiceSearchInput, setTechServiceSearchInput] = useState('');
  const [techServiceSearchResults, setTechServiceSearchResults] = useState([]);
  const [techServiceSearchLoading, setTechServiceSearchLoading] = useState(false);
  const [techServiceSearchDebouncing, setTechServiceSearchDebouncing] = useState(false);
  const [techServicePickerOpen, setTechServicePickerOpen] = useState(false);
  const [selectedTechServiceName, setSelectedTechServiceName] = useState('');
  const [selectedTechService, setSelectedTechService] = useState(null);

  /* Seçim yapılınca dropdown kapanır; arama metni servis adıyla aynıysa tekrar açılmaz */
  const hideTechServiceSearchDropdown = useMemo(() => {
    if (!techServicePickerOpen) return true;
    if (!selectedTechService) return false;
    const ad = (selectedTechService.ad || '').trim();
    if (!ad) return false;
    return techServiceSearchInput.trim() === ad;
  }, [selectedTechService, techServiceSearchInput, techServicePickerOpen]);

  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      setPageError('');
      try {
        const response = await fetchUserById(id);
        const user = response?.veri?.kullanici;
        if (!user) {
          throw new Error('Kullanıcı bulunamadı');
        }
        
        // Debug: Backend'den gelen kullanıcı verisini logla
        console.log('[UserFormPage] User data from backend:', {
          user,
          techService: user.techService,
          techServiceType: typeof user.techService,
          techServiceIsObject: typeof user.techService === 'object',
          techServiceId: user.techService?._id || user.techService?.id || user.techService,
        });
        
        // techService ID'sini al - obje ise _id veya id'yi al, string ise direkt kullan
        let techServiceId = '';
        if (user.techService) {
          if (typeof user.techService === 'object') {
            techServiceId = user.techService._id || user.techService.id || '';
          } else {
            techServiceId = String(user.techService);
          }
        }
        
        setFormState({
          id: user._id || user.id || '',
          kullaniciAdi: user.kullaniciAdi || '',
          sifre: '',
          rol: user.rol || 'sales',
          techService: techServiceId,
          aktif:
            user.aktif !== undefined
              ? Boolean(user.aktif)
              : user.durum !== undefined
                ? user.durum === 1
                : true,
          isTest: user.isTest === true,
          ad: user.ad || '',
          soyad: user.soyad || '',
          bayiAdi: user.bayiAdi || '',
          mail: user.mail || '',
          telefon: user.telefon || '',
          orderCreationPermission:
            user.orderCreationPermission || ORDER_CREATION_PERMISSION_DEFAULT,
        });

        if (techServiceId) {
          try {
            console.log('[UserFormPage] Loading tech service detail for ID:', techServiceId);
            const detail = await fetchTechServiceDetail(techServiceId);
            const techServiceData = detail?.veri?.teknikServis || detail?.veri || null;
            const name =
              techServiceData?.ad ||
              detail?.veri?.ad ||
              '';
            console.log('[UserFormPage] Tech service loaded:', {
              techServiceData,
              name,
            });
            setSelectedTechServiceName(name);
            setSelectedTechService(techServiceData);
            setTechServiceSearchInput(''); // Arama kutusunu boş bırak, sadece seçili teknik servis gösterilsin
          } catch (innerError) {
            console.warn('Teknik servis adı alınamadı:', innerError);
            setSelectedTechServiceName('');
            setSelectedTechService(null);
            setTechServiceSearchInput('');
          }
        } else {
          console.log('[UserFormPage] No tech service found for user');
          setSelectedTechServiceName('');
          setSelectedTechService(null);
          setTechServiceSearchInput('');
        }
      } catch (error) {
        setPageError(getErrorMessage(error, 'Kullanıcı bilgileri yüklenemedi'));
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  useEffect(() => {
    const term = techServiceSearchInput.trim();
    if (term.length < 3) {
      setTechServiceSearchDebouncing(false);
      return;
    }
    setTechServiceSearchDebouncing(true);
    const tid = setTimeout(() => setTechServiceSearchDebouncing(false), 400);
    return () => clearTimeout(tid);
  }, [techServiceSearchInput]);

  useEffect(() => {
    if (!techServiceSearchInput || techServiceSearchInput.trim().length < 3) {
      setTechServiceSearchResults([]);
      setTechServiceSearchLoading(false);
      return;
    }

    const term = techServiceSearchInput.trim();
    const selectedAd = (selectedTechService?.ad || '').trim();
    if (selectedTechService && term === selectedAd) {
      setTechServiceSearchResults([]);
      setTechServiceSearchLoading(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setTechServiceSearchLoading(true);
      try {
        const response = await searchTechServices(term, {
          aktif: true,
        });
        const list = response?.veri?.teknikServisler || [];
        setTechServiceSearchResults(list);
      } catch (error) {
        console.warn('Teknik servis arama hatası:', error);
        setTechServiceSearchResults([]);
      } finally {
        setTechServiceSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [techServiceSearchInput, selectedTechService]);

  const handleSectionAlert = (sectionKey) => {
    const section = sectionStatus[sectionKey];
    if (!section) {
      return null;
    }

    const statusClass = section.status === 'success' ? 'success' : 'error';
    return (
      <div className={`section-alert ${statusClass}`}>
        {safeText(section.message)}
      </div>
    );
  };

  const handleTechServiceSelect = (service) => {
    const techId = service.id || service._id;
    if (!techId) return;
    setFormState((prev) => ({
      ...prev,
      techService: techId,
    }));
    setSelectedTechServiceName(service.ad || 'Teknik servis');
    setSelectedTechService(service);
    setTechServiceSearchResults([]);
    setTechServiceSearchInput(service.ad || '');
    setTechServicePickerOpen(false);
  };

  const clearTechServiceSelection = async () => {
    const ok = await confirm({
      title: 'Seçimi temizle',
      message: 'Teknik servis seçimini temizlemek istediğinize emin misiniz?',
      variant: 'warning',
    });
    if (!ok) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      techService: '',
    }));
    setSelectedTechServiceName('');
    setTechServiceSearchInput('');
    setTechServiceSearchResults([]);
    setSelectedTechService(null);
    setTechServicePickerOpen(false);
  };

  const handleSectionSave = async (sectionKey) => {
    setSavingSection(sectionKey);
    setSectionStatus((prev) => ({ ...prev, [sectionKey]: null }));

    try {
      if (sectionKey === 'credentials') {
        const username = formState.kullaniciAdi?.trim() || '';
        if (username.length < 3 || username.length > 50) {
          throw new Error('Kullanıcı adı 3-50 karakter arasında olmalıdır');
        }

        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
          throw new Error('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir');
        }

        if (!formState.rol) {
          throw new Error('Rol bilgisi eksik');
        }

        if (
          (formState.rol === 'tech-person' || formState.rol === 'tech-service') &&
          !formState.techService
        ) {
          throw new Error('Teknik servis veya teknisyen rolü için servis seçimi gereklidir');
        }

        const payload = {
          kullaniciAdi: username,
          aktif: Boolean(formState.aktif),
          isTest: Boolean(formState.isTest),
        };

        if (formState.sifre && formState.sifre.trim().length > 0) {
          const password = formState.sifre.trim();
          if (password.length < 6) {
            throw new Error('Şifre en az 6 karakter olmalıdır');
          }
          if (password.length > 100) {
            throw new Error('Şifre en fazla 100 karakter olabilir');
          }
          if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
            throw new Error('Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermelidir');
          }
          payload.sifre = password;
        }

        if (formState.rol === 'tech-person') {
          payload.orderCreationPermission =
            formState.orderCreationPermission || ORDER_CREATION_PERMISSION_DEFAULT;
        }

        if (formState.rol === 'bayi') {
          const bayiAdi = formState.bayiAdi?.trim() || '';
          if (!bayiAdi) {
            throw new Error('Bayi adı zorunludur');
          }
          payload.bayiAdi = bayiAdi;
        }

        if (formState.techService) {
          payload.techService = formState.techService;
        } else if (formState.rol !== 'tech-person' && formState.rol !== 'tech-service') {
          payload.techService = null;
        }

        await updateUser(formState.id, payload);
        
        // Teknik servis bilgisini güncelle
        if (formState.techService) {
          try {
            const detail = await fetchTechServiceDetail(formState.techService);
            const techServiceData = detail?.veri?.teknikServis || detail?.veri || null;
            const name =
              techServiceData?.ad ||
              detail?.veri?.ad ||
              '';
            setSelectedTechServiceName(name);
            setSelectedTechService(techServiceData);
            setTechServiceSearchInput(''); // Arama kutusunu boş bırak, sadece seçili teknik servis gösterilsin
          } catch (innerError) {
            console.warn('Teknik servis bilgisi güncellenemedi:', innerError);
          }
        } else {
          setSelectedTechServiceName('');
          setSelectedTechService(null);
          setTechServiceSearchInput('');
        }
        
        setSectionStatus((prev) => ({
          ...prev,
          [sectionKey]: {
            status: 'success',
            message: 'Giriş bilgileri güncellendi',
          },
        }));
        setFormState((prev) => ({ ...prev, sifre: '' }));
        if (window.showNotification) {
          window.showNotification('success', 'Giriş bilgileri güncellendi');
        }
      }

      if (sectionKey === 'personal') {
        const firstName = formState.ad?.trim() || '';
        const lastName = formState.soyad?.trim() || '';
        
        // Max length validation (UX için)
        if (firstName.length > 50) {
          throw new Error('Ad en fazla 50 karakter olabilir');
        }
        if (lastName.length > 50) {
          throw new Error('Soyad en fazla 50 karakter olabilir');
        }
        
        const rolesRequiringName = ['admin', 'mod', 'sales', 'sevkiyat', 'bayi'];
        if (rolesRequiringName.includes(formState.rol)) {
          if (!firstName || !lastName) {
            throw new Error('Ad ve soyad alanları doldurulmalıdır');
          }
        }

        if (formState.mail && !validateEmail(formState.mail.trim())) {
          throw new Error('Geçerli bir e-posta adresi giriniz');
        }

        if (formState.telefon && !PHONE_REGEX.test(formState.telefon.trim().replace(/\s/g, ''))) {
          throw new Error('Geçerli bir telefon numarası giriniz (örn: 5551234567)');
        }

        const payload = {
          ad: firstName,
          soyad: lastName,
          mail: formState.mail ? formState.mail.trim().toLowerCase() : undefined,
          telefon: formState.telefon ? formState.telefon.trim() : undefined,
        };

        await updateUser(formState.id, payload);
        setSectionStatus((prev) => ({
          ...prev,
          [sectionKey]: {
            status: 'success',
            message: 'Kişisel bilgiler güncellendi',
          },
        }));
        setFormState((prev) => ({
          ...prev,
          ad: firstName,
          soyad: lastName,
          mail: formState.mail ? formState.mail.trim().toLowerCase() : '',
          telefon: formState.telefon ? formState.telefon.trim() : '',
        }));
        if (window.showNotification) {
          window.showNotification('success', 'Kişisel bilgiler güncellendi');
        }
      }
    } catch (error) {
      setSectionStatus((prev) => ({
        ...prev,
        [sectionKey]: {
          status: 'error',
          message: getErrorMessage(error, 'Güncelleme sırasında hata oluştu'),
        },
      }));
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="content-header">
          <h1>Kullanıcı Bilgileri</h1>
        </div>
        <div className="content-wrapper">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Kullanıcı yükleniyor</div>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content user-form-page">
      <div className="content-header">
        <div>
          <h1>Kullanıcı Düzenle</h1>
        </div>
        <button
          type="button"
          className="secondary-btn"
          onClick={() => navigate('/users')}
        >
          <i className="fas fa-arrow-left"></i>
          Listeye Dön
        </button>
      </div>

      {pageError && (
        <div className="error-state" style={{ marginBottom: '16px' }}>
          {safeText(pageError)}
        </div>
      )}

      <div className="tabs">
        {USER_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-panel">
        {activeTab === 'credentials' && (
          <div className="tab-panel-card">
            <div className="section-block">
              <div className="section-title">
                Giriş Bilgileri
              </div>
              {handleSectionAlert('credentials')}
              <div className="form-grid">
                <label className="form-field">
                  <span>Kullanıcı Adı *</span>
                  <input
                    type="text"
                    name="kullaniciAdi"
                    value={formState.kullaniciAdi}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        kullaniciAdi: event.target.value,
                      }))
                    }
                    maxLength={50}
                    minLength={3}
                    pattern="[a-zA-Z0-9_]+"
                    placeholder="Kullanıcı Adı"
                  />
                  <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    3-50 karakter; sadece harf, rakam ve alt çizgi
                  </small>
                </label>
                <label className="form-field">
                  <span>Şifre</span>
                  <input
                    type="password"
                    name="sifre"
                    value={formState.sifre}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        sifre: event.target.value,
                      }))
                    }
                    placeholder="Boş bırakırsanız değişmez"
                    minLength={6}
                    maxLength={100}
                  />
                  {formState.sifre ? (
                    <small style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px', display: 'block' }}>
                      {formState.sifre.length} / 100 karakter - Küçük harf, büyük harf ve rakam içermeli
                    </small>
                  ) : (
                    <small style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px', display: 'block' }}>
                      Şifreyi değiştirmek istemiyorsanız alana bir şey yazmayın. Küçük harf, büyük harf ve rakam içermeli.
                  </small>
                  )}
                </label>
                <div className="form-field">
                  <span>Rol</span>
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--bg-subtle, rgba(0,0,0,0.03))',
                      fontSize: '14px',
                      color: 'var(--text-dark)',
                    }}
                  >
                    {roleOptions.find((o) => o.value === formState.rol)?.label || formState.rol}
                  </div>
                  <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Rol düzenleme sırasında değiştirilemez; farklı rol için yeni kullanıcı oluşturun.
                  </small>
                </div>
                {formState.rol === 'bayi' && (
                  <label className="form-field full-width">
                    <span>Bayi İsmi *</span>
                    <input
                      type="text"
                      name="bayiAdi"
                      value={formState.bayiAdi}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          bayiAdi: event.target.value,
                        }))
                      }
                      placeholder="Bayinin tam adı"
                      maxLength={200}
                      required
                    />
                  </label>
                )}
                {(formState.rol === 'tech-person' ||
                  formState.rol === 'tech-service') && (
                  <div className="form-field full-width">
                    <span>Teknik Servis *</span>
                    <div className="tech-service-selection tech-service-selection--picker">
                      <div className="tech-service-selection-input">
                        <div className="search-box">
                          <i className="fas fa-search"></i>
                          <input
                            type="text"
                            placeholder="Örn: şehir veya servis adı (min 3 karakter, boşluk kullanılabilir)"
                            value={techServiceSearchInput}
                            onChange={(event) => {
                              setTechServiceSearchInput(
                                sanitizeSearchInput(event.target.value, {
                                  trimEdges: false,
                                })
                              );
                              if (event.target.value.trim().length >= 3) {
                                setTechServicePickerOpen(true);
                              }
                            }}
                            autoComplete="off"
                          />
                          {techServiceSearchInput && (
                            <button
                              type="button"
                              className="search-clear-btn"
                              onClick={() => setTechServiceSearchInput('')}
                              aria-label="Aramayı temizle"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      {techServiceSearchInput.trim().length >= 3 && !hideTechServiceSearchDropdown && (
                        <div
                          className="tech-service-selection-dropdown"
                          role="listbox"
                          aria-label="Teknik servis arama sonuçları"
                        >
                          <div className="tech-service-selection-list" role="list">
                            {techServiceSearchDebouncing || techServiceSearchLoading ? (
                              <div className="tech-service-selection-empty tech-service-selection-empty--loading">
                                <span className="ts-picker-spinner" aria-hidden="true" />
                                <span>Sonuçlar getiriliyor…</span>
                              </div>
                            ) : techServiceSearchResults.length === 0 ? (
                              <div className="tech-service-selection-empty tech-service-selection-empty--hint">
                                <i className="fas fa-folder-open"></i>
                                <div className="ts-picker-empty-text">
                                  <span className="ts-picker-empty-title">Sonuç yok</span>
                                  <span className="ts-picker-empty-sub">Farklı bir terim deneyin.</span>
                                </div>
                              </div>
                            ) : (
                              techServiceSearchResults.map((service) => {
                                const serviceId = service.id || service._id;
                                const isSelected =
                                  formState.techService &&
                                  String(formState.techService) === String(serviceId);
                                const displayName = service.ad || 'Bilinmeyen Teknik Servis';
                                const loc = formatTechServiceLocation(service);
                                return (
                                  <button
                                    type="button"
                                    key={serviceId}
                                    className={`tech-service-selection-item ts-picker-row ${
                                      isSelected ? 'active' : ''
                                    }`}
                                    onClick={() => handleTechServiceSelect(service)}
                                  >
                                    <div className="ts-picker-row-body">
                                      <div className="ts-picker-row-top">
                                        <span className="ts-picker-name">{displayName}</span>
                                        {service.kod != null && (
                                          <span className="ts-picker-badge">Kod {service.kod}</span>
                                        )}
                                      </div>
                                      {loc ? (
                                        <div className="ts-picker-loc">
                                          <i className="fas fa-map-marker-alt" aria-hidden="true" />
                                          <span>{loc}</span>
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="ts-picker-check is-on" aria-hidden="true">
                                      <i className="fas fa-check" />
                                    </div>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                      {selectedTechService && (
                        <div className="selected-tech-service">
                          <div>
                            <span className="tech-service-selection-title">
                              Seçili: {selectedTechServiceName}
                            </span>
                            <div className="tech-service-selection-subtitle">
                              {selectedTechService?.kod != null
                                ? `Kod: ${selectedTechService.kod} · `
                                : ''}
                              {formatTechServiceLocation(selectedTechService)}
                            </div>
                          </div>
                          <div className="selected-tech-service-actions">
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => {
                                const techId =
                                  selectedTechService.id || selectedTechService._id;
                                if (techId) {
                                  navigate(`/tech-services/${techId}/edit`);
                                }
                              }}
                            >
                              Teknik servisi düzenle
                            </button>
                            <button
                              type="button"
                              className="secondary-btn"
                              onClick={clearTechServiceSelection}
                            >
                              <i className="fas fa-trash"></i>
                              Temizle
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      Teknisyen ve teknik servis rollerinde servis seçimi zorunludur
                    </small>
                  </div>
                )}
                {formState.rol === 'tech-person' && (
                  <label className="form-field full-width">
                    <span>Sipariş Oluşturma Yetkisi</span>
                    <select
                      name="orderCreationPermission"
                      value={formState.orderCreationPermission}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          orderCreationPermission: event.target.value,
                        }))
                      }
                    >
                      {ORDER_CREATION_PERMISSION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="form-field checkbox-field">
                  <input
                    type="checkbox"
                    name="aktif"
                    checked={formState.aktif}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        aktif: event.target.checked,
                      }))
                    }
                  />
                  <span>Aktif Kullanıcı</span>
                </label>
                <label className="form-field checkbox-field">
                  <input
                    type="checkbox"
                    name="isTest"
                    checked={formState.isTest}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        isTest: event.target.checked,
                      }))
                    }
                  />
                  <span>Test kullanıcısı</span>
                </label>
              </div>
            </div>
            <div className="section-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={() => handleSectionSave('credentials')}
                disabled={savingSection === 'credentials'}
              >
                {savingSection === 'credentials' ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'personal' && (
          <div className="tab-panel-card">
            <div className="section-block">
              <div className="section-title">Kişisel Bilgiler</div>
              {handleSectionAlert('personal')}
              <div className="form-grid">
                <label className="form-field">
                  <span>Ad</span>
                  <input
                    type="text"
                    name="ad"
                    value={formState.ad}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, ad: event.target.value }))
                    }
                    placeholder="Ad"
                    maxLength={50}
                  />
                  {formState.ad && (
                    <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                      {formState.ad.length} / 50 karakter
                    </small>
                  )}
                </label>
                <label className="form-field">
                  <span>Soyad</span>
                  <input
                    type="text"
                    name="soyad"
                    value={formState.soyad}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, soyad: event.target.value }))
                    }
                    placeholder="Soyad"
                    maxLength={50}
                  />
                  {formState.soyad && (
                    <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                      {formState.soyad.length} / 50 karakter
                    </small>
                  )}
                </label>
                <label className="form-field">
                  <span>E-posta</span>
                  <input
                    type="email"
                    name="mail"
                    value={formState.mail}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, mail: event.target.value }))
                    }
                    placeholder="ornek@email.com"
                  />
                </label>
                <label className="form-field">
                  <span>Telefon</span>
                  <input
                    type="tel"
                    name="telefon"
                    value={formState.telefon}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        telefon: event.target.value,
                      }))
                    }
                    placeholder="5551234567 veya +905551234567"
                  />
                </label>
              </div>
            </div>
            <div className="section-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={() => handleSectionSave('personal')}
                disabled={savingSection === 'personal'}
              >
                {savingSection === 'personal' ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default UserFormPage;

